import { useMemo } from "react";
import {
    getClientRect,
    KeyboardSensor,
    MeasuringStrategy,
    useSensor,
    useSensors,
    type DndContextProps,
    type KeyboardSensorOptions,
} from "@dnd-kit/core";
import { SILENT_DND_ANNOUNCEMENTS } from "@design-system/LiveRegion/silentDndAnnouncements";
import { BOARD_DRAG_INSTRUCTIONS } from "./boardAnnouncements";
import type { BoardCells } from "./boardCells";
import { getLandingRect } from "./boardLanding";
import { BOARD_ARROW_DIRECTIONS } from "./boardDropTargets";
import {
    BOARD_KEYBOARD_CODES,
    BOARD_POINTER_ACTIVATION,
    BoardPointerSensor,
    createBoardCollisionDetection,
    getCellDroppableId,
    isPlaceModifier,
} from "./boardSensors";
import type { BoardKeyboardStep } from "./useBoardKeyboardStep";

const ACCESSIBILITY: DndContextProps["accessibility"] = {
    announcements: SILENT_DND_ANNOUNCEMENTS,
    screenReaderInstructions: { draggable: BOARD_DRAG_INSTRUCTIONS },
    restoreFocus: false,
};

// Drop zones are measured only while dragging: at pick-up, and again whenever
// the preview moves slots or a windowed list mounts cards. `Always` would
// also re-measure every droppable at rest, each time a scrolled-in chunk
// mounts.
const DROPPABLE_MEASURING = { strategy: MeasuringStrategy.WhileDragging };

// dnd-kit's default for draggables.
const measureIgnoringTransform = (node: HTMLElement) =>
    getClientRect(node, { ignoreTransform: true });

const POINTER_OPTIONS = { activationConstraint: BOARD_POINTER_ACTIVATION };

// A card previewed in a collapsed column unmounts; when the column opens
// under the pointer it mounts again, and dnd-kit's layout shift compensation
// would scroll by how far it is from where it was picked up.
const AUTO_SCROLL = { layoutShiftCompensation: false };

interface UseBoardDndConfigArgs<TItem> {
    renderedCells: BoardCells<TItem>;
    stepKeyboard: (step: BoardKeyboardStep) => void;
    /** A pointer drop is landing: the dragged card is measured where it will show. */
    isSettling: () => boolean;
}

/**
 * Sensors and collision detection for the board's `DndContext`. Keyboard
 * moves are resolved on the board model, not by nudging coordinates: the
 * getter steps the target and returns nothing, so the card itself moves
 * slot by slot and no overlay has to be positioned.
 */
export const useBoardDndConfig = <TItem>({
    renderedCells,
    stepKeyboard,
    isSettling,
}: UseBoardDndConfigArgs<TItem>) => {
    const keyboardOptions = useMemo(
        (): KeyboardSensorOptions => ({
            keyboardCodes: BOARD_KEYBOARD_CODES,
            coordinateGetter: (event) => {
                const direction = BOARD_ARROW_DIRECTIONS[event.code];
                if (direction !== undefined) {
                    event.preventDefault();
                    stepKeyboard({
                        direction,
                        isPlaceHeld: isPlaceModifier(event),
                    });
                }
                return undefined;
            },
        }),
        [stepKeyboard],
    );
    const sensors = useSensors(
        useSensor(BoardPointerSensor, POINTER_OPTIONS),
        useSensor(KeyboardSensor, keyboardOptions),
    );

    const collisionDetection = useMemo(() => {
        const cellIdsByPlacementKey = new Map<string, string>();
        renderedCells.entriesByCell.forEach((entries) =>
            entries.forEach((entry) =>
                cellIdsByPlacementKey.set(
                    entry.placementKey,
                    getCellDroppableId(entry.placement),
                ),
            ),
        );
        return createBoardCollisionDetection({
            isCellDroppable: (id) => !cellIdsByPlacementKey.has(String(id)),
            getCellDroppableIdOfCard: (id) =>
                cellIdsByPlacementKey.get(String(id)),
        });
    }, [renderedCells]);

    const measuring = useMemo(
        (): DndContextProps["measuring"] => ({
            droppable: DROPPABLE_MEASURING,
            draggable: {
                measure: (node) => {
                    const rect = measureIgnoringTransform(node);
                    return isSettling() ? getLandingRect(node, rect) : rect;
                },
            },
        }),
        [isSettling],
    );

    return {
        sensors,
        collisionDetection,
        measuring,
        accessibility: ACCESSIBILITY,
        autoScroll: AUTO_SCROLL,
    };
};
